import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, DialogActions } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import { LoadingButton } from '~/modules/common/components';
import { usePutAllocateResourceDialogSettings } from './hooks/useAllocateResourceDialogSettings';
import useAllocateResourcePeriodError from './hooks/useAllocateResourcePeriodError';
import { FIELDS } from './hooks/useOnChangeHandlers';

const useStyles = makeStyles(theme => ({
  actionButton: {
    flexGrow: 0,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shortest
    }),
    marginRight: theme.spacing(1)
  },
  footer: {
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap'
  },
  settingsButton: {
    marginRight: 'auto'
  },
  settingsButtonFullWidth: {
    width: '100%'
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
  }
}));

const AllocateResourceDialogFooter = ({
  onClose,
  isSaving,
  isLoading,
  isSmartFitOpen,
  focusField
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { values, setFieldValue, handleSubmit } = useFormikContext();
  const {
    putAllocateResourceDialogSettings
  } = usePutAllocateResourceDialogSettings();

  const {
    totalAllocatedHours,
    load,
    user,
    project,
    showAdvancedSettings,
    selectedAllocationPeriod,
    displayUnit
  } = values;

  const { allocationError } = useAllocateResourcePeriodError();

  const isSavingOrSmartFitOpen = isSmartFitOpen || isSaving;
  const allocateButtonRef = useRef(null);

  useEffect(() => {
    if (!isSmartFitOpen && focusField === FIELDS.SUBMIT_BUTTON) {
      allocateButtonRef.current?.focus();
    }
  }, [isSmartFitOpen, focusField]);

  const [settingsAriaLabel, setSettingsAriaLabel] = useState('');

  const onToggleSettings = useCallback(() => {
    const newValue = !showAdvancedSettings;

    setFieldValue('showAdvancedSettings', newValue);
    putAllocateResourceDialogSettings({
      showAdvancedSettings: newValue,
      selectedAllocationPeriod,
      displayUnit
    });
    setSettingsAriaLabel(
      formatMessage({
        id: newValue
          ? 'allocateResourcesContent.advancedSettingsAriaLabel'
          : 'allocateResourcesContent.basicSettingsAriaLabel'
      })
    );
  }, [
    setFieldValue,
    showAdvancedSettings,
    selectedAllocationPeriod,
    displayUnit,
    putAllocateResourceDialogSettings,
    formatMessage
  ]);

  const isDisabled =
    isSavingOrSmartFitOpen ||
    isLoading ||
    !user ||
    !project ||
    (!showAdvancedSettings && (load && totalAllocatedHours) === 0) ||
    Boolean(allocationError);

  return (
    <DialogActions className={classes.footer}>
      <span aria-live="polite" className={classes.srOnly}>
        {settingsAriaLabel}
      </span>
      <div
        className={classNames(classes.settingsButton, {
          [classes.settingsButtonFullWidth]: !showAdvancedSettings
        })}
      >
        <Button
          data-qe-id="AllocateResourceSettingsButton"
          onClick={onToggleSettings}
          variant="text"
          color="primary"
        >
          <FormattedMessage
            id={
              showAdvancedSettings
                ? 'allocateResourcesContent.basicSettings'
                : 'allocateResourcesContent.advancedSettings'
            }
          />
        </Button>
      </div>
      <Button
        data-qe-id="AllocateResourceCancelButton"
        disabled={isSavingOrSmartFitOpen}
        onClick={onClose}
        variant="contained"
      >
        <FormattedMessage id="allocateResourcesContent.cancel" />
      </Button>
      <LoadingButton
        ref={allocateButtonRef}
        data-qe-id="AllocateResourceAddButton"
        className={classes.actionButton}
        color="primary"
        onClick={handleSubmit}
        variant="contained"
        isLoading={isSavingOrSmartFitOpen}
        disabled={isDisabled}
      >
        <FormattedMessage id="allocateResourcesContent.allocateResource" />
      </LoadingButton>
    </DialogActions>
  );
};

AllocateResourceDialogFooter.propTypes = {
  onClose: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSmartFitOpen: PropTypes.bool,
  focusField: PropTypes.string
};

export default AllocateResourceDialogFooter;
